import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './BookDetails.css';
import { API_ENDPOINTS } from '../../config/api';
import LogoLoader from '../../components/LogoLoader';
import { renderTiptapHTML } from '../../Blog/tiptapRenderer';
import { robustFetch } from '../../utils/apiUtils';

const BookDetails = () => {
    const { bookId } = useParams();
    const navigate = useNavigate();
    const [book, setBook] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        const fetchBook = async () => {
            try {
                const response = await robustFetch(API_ENDPOINTS.AFFILIATE_PRODUCTS.GET_BOOK(bookId));
                const result = await response.json();
                if (result.success) {
                    setBook(result.data);
                } else {
                    setError(result.message || 'Book not found');
                }
            } catch (err) {
                setError('Failed to fetch book details');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (bookId) fetchBook();
    }, [bookId]);

    const nextImage = (e) => {
        e.stopPropagation();
        if (book?.productImages?.length > 0) {
            setCurrentImageIndex((prev) => (prev + 1) % book.productImages.length);
        }
    };

    const prevImage = (e) => {
        e.stopPropagation();
        if (book?.productImages?.length > 0) {
            setCurrentImageIndex((prev) => (prev - 1 + book.productImages.length) % book.productImages.length);
        }
    };

    if (loading) return <LogoLoader progress={70} />;
    if (error || !book) {
        return (
            <div className="book-details-error">
                <h2>Error</h2>
                <p>{error || 'Book not found'}</p>
                <button onClick={() => navigate('/app-store/bookstore')}>Back to Store</button>
            </div>
        );
    }

    return (
        <div className="book-details-page immersive">
            <Helmet>
                <title>{book.productName} | Book Store | Hari Vishnu</title>
                <meta name="description" content={book.productName} />
            </Helmet>

            <div className="immersive-layout">
                {/* Left Side: The "Cover" and Intro */}
                <div className="immersive-left">
                    <div className="intro-content">
                        <div className="book-gallery">
                            <div className="book-image-main">
                                <img 
                                    src={book.productImages?.[currentImageIndex]?.url} 
                                    alt={book.productName} 
                                    className="main-cover"
                                />
                                {book.productImages?.length > 1 && (
                                    <>
                                        <button className="gallery-arrow prev" onClick={prevImage}>
                                            <i className="fi fi-rr-angle-left"></i>
                                        </button>
                                        <button className="gallery-arrow next" onClick={nextImage}>
                                            <i className="fi fi-rr-angle-right"></i>
                                        </button>
                                    </>
                                )}
                            </div>
                            {book.productImages?.length > 1 && (
                                <div className="gallery-dots">
                                    {book.productImages.map((_, i) => (
                                        <div 
                                            key={i} 
                                            className={`gallery-dot ${i === currentImageIndex ? 'active' : ''}`}
                                            onClick={() => setCurrentImageIndex(i)}
                                        ></div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <h1 className="immersive-title">{book.productName}</h1>
                        <p className="immersive-price">₹{book.price}</p>
                        
                        <div className="immersive-meta">
                            <span className="author-label">Review by</span>
                            <span className="author-name">Hari Vishnu</span>
                        </div>
                    </div>
                </div>

                {/* Right Side: The "Content" and Action */}
                <div className="immersive-right">
                    <div className="immersive-header">
                        <div className="reader-status">
                            <span className="opinion-text">What is my opinion on this book</span>
                        </div>
                        <div className="header-actions">
                            <button className="close-book-btn" onClick={() => navigate('/app-store/bookstore')}>
                                Close book
                            </button>
                        </div>
                    </div>

                    <div className="immersive-content-scroll">
                        <div className="reading-area">
                            <div className="content-container">
                                {book.productDescription ? (
                                    <div 
                                        className="tiptap-read-only immersive-text"
                                        dangerouslySetInnerHTML={{ __html: renderTiptapHTML(book.productDescription) }}
                                    />
                                ) : (
                                    <p className="no-desc">No detailed description available for this book.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="immersive-bottom-bar">
                        <a 
                            href={book.affiliateLink} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="immersive-buy-btn"
                        >
                            Get this Book <i className="fi fi-rr-arrow-small-right"></i>
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookDetails;
